if (!customElements.get('store-selector')) {
  customElements.define(
    'store-selector',
    class StoreSelector extends window.BAO.CustomElement() {
      connectedCallback () {
        super.connectedCallback()

        // eslint-disable-next-line compat/compat
        const params = new Proxy(new URLSearchParams(window.location.search), {
          get: (searchParams, prop) => searchParams.get(prop),
        })
        const countryParam = params.country

        this.removeCountryParam()

        if (!this.noChoiceMade) {
          this.setupCountrySwitcherModal(
            this.displayCode(window.localStorage.getItem(this.userLocationName)),
          )
          return
        }

        if (countryParam !== null) {
          this.setStorageFromURL()
          this.setupCountrySwitcher(this.dataset.store)
          return
        }

        window.BAO.Utils.fetchJSON(`/browsing_context_suggestions.json`, {
          method: 'GET',
        }).then(res => {
          this.setupCountrySwitcherModal(res.detected_values.country.handle)
        })
      }

      setupListeners () {
        super.setupListeners()

        this.onConfirmClick = this.onConfirmClick.bind(this)
        this.onCloseClick = this.onCloseClick.bind(this)
        this.onOptionsClick = this.onOptionsClick.bind(this)

        if (this.els?.options.exists) {
          this.listeners.add(
            this.els.options.element,
            'change',
            this.onOptionsClick,
          )
        }

        if (this.els?.confirm.exists) {
          this.els.confirm.elements.forEach(confirmEl => {
            this.listeners.add(confirmEl, 'click', this.onConfirmClick)
          })
        }

        if (this.els?.close.exists) {
          this.els.close.elements.forEach(closeEl => {
            this.listeners.add(closeEl, 'click', this.onCloseClick)
          })
        }
      }

      setStorageFromURL () {
        window.localStorage.setItem(this.storageName, 'true')
        window.localStorage.setItem(this.userLocationName, this.dataset.store)
      }

      localisationCode (code) {
        return code === 'EU' ? 'DE' : code
      }

      displayCode (code) {
        return code === 'DE' && this.dataset.store === 'EU' ? 'EU' : code
      }

      removeCountryParam () {
        // eslint-disable-next-line compat/compat
        const url = new URL(window.location.href)
        if (url.searchParams.get('country') !== 'DE') return

        url.searchParams.delete('country')
        window.history.replaceState({}, '', url.toString())
      }

      onOptionsClick () {
        const selectedOptionValue = this.els.options.element.value
        const selectedOptionCode = this.els.options.element.options[
          this.els.options.element.selectedIndex
        ].dataset.code

        this.handleFlag(selectedOptionCode)

        this.els.confirm.elements.forEach(confirmEl => {
          confirmEl.dataset.url = this.stores[selectedOptionValue]
          confirmEl.dataset.code = selectedOptionCode
        })
      }

      isEuCountry (code) {
        const euCodes = this.dataset.euCodes.split(',')
        return euCodes.includes(code.toUpperCase())
      }

      handleFlag (code) {
        if (code.toLowerCase() === `eu`) {
          this.flag.classList.add('sec-StoreSelector_Flag-hidden')
        } else {
          const newFlag = this.replaceFlag(code)
          this.flag.setAttribute('src', newFlag)
          this.flag.setAttribute('srcset', newFlag)
          this.flag.setAttribute('alt', code)
          this.flag.classList.remove('sec-StoreSelector_Flag-hidden')
        }
      }

      onConfirmClick (event) {
        event.preventDefault()

        const { code, url } = event.currentTarget.dataset

        window.localStorage.setItem(this.storageName, 'true')
        window.localStorage.setItem(this.userLocationName, code)

        if (!url) {
          this.els?.close?.element?.click()
          return
        }

        const query =
          this.els.options.element.value === 'EU'
            ? `?country=${this.localisationCode(code)}`
            : ''
        const destination = `${url.replace(/\/$/, '')}${
          window.location.pathname
        }${query}`

        if (destination === window.location.href) {
          this.els?.close?.element?.click()
          return
        }

        window.location.href = destination
      }

      onCloseClick (event) {
        event.preventDefault()

        window.localStorage.setItem(this.storageName, 'true')
        window.localStorage.setItem(this.userLocationName, this.dataset.store)
      }

      setupCountrySwitcher (countryCode) {
        this.handleFlag(countryCode)
        this.createOptionsList(countryCode)
        this.setDefaultConfirmButtonURL()
      }

      setupCountrySwitcherModal (countryCode) {
        if (countryCode === this.dataset.store) {
          window.localStorage.setItem(this.storageName, 'true')
          window.localStorage.setItem(this.userLocationName, countryCode)
        } else {
          this.showModal()
        }
        this.setupCountrySwitcher(countryCode)
      }

      showModal () {
        window.BAO.dispatchEvent(window.BAO.EVENTS.MODAL.LOCATION)
        this.setAttribute('aria-hidden', false)
      }

      createOptionsList (countryCode) {
        Object.entries(this.countries).forEach(([key, value]) => {
          const { currency, name, store } = value
          const isCurrentStore = key.toLowerCase() === countryCode.toLowerCase()
          this.els.options.element.innerHTML += this.createOptionElement(
            key,
            store,
            isCurrentStore,
            name,
            currency,
          )
        })
      }

      createOptionElement (code, store, isCurrentStore, country, currency) {
        return `<option
            class="sec-StoreSelector_Option"
            data-code="${code}"
            value="${store}"
            ${isCurrentStore ? 'selected' : ''}
          >
            ${country} / ${currency}
          </option>`
      }

      replaceFlag (code) {
        return `https://cdn.shopify.com/static/images/flags/${code.toLowerCase()}.svg?crop=center&height=20&width=30`
      }

      setDefaultConfirmButtonURL () {
        const selectedOptionValue = this.els.options.element.value
        const selectedOptionCode = this.els.options.element.options[
          this.els.options.element.selectedIndex
        ].dataset.code

        this.els.confirm.elements.forEach(confirmEl => {
          confirmEl.dataset.url = this.stores[selectedOptionValue]
          confirmEl.dataset.code = selectedOptionCode
        })
      }

      get storeSelectorTrigger () {
        return window.BAO.Utils.getElement('[data-store-selector-trigger]')
      }

      get currentCountryList () {
        return this.getAttribute('switcher-country-list')
      }

      get noChoiceMade () {
        return (
          window.localStorage.getItem(this.storageName) == null ||
          (window.localStorage.getItem(this.storageName) === undefined &&
            !Shopify.designMode)
        )
      }

      get noLocationStored () {
        return (
          window.localStorage.getItem(this.userLocationName) == null ||
          (window.localStorage.getItem(this.userLocationName) === undefined &&
            !Shopify.designMode)
        )
      }

      get storageName () {
        return `BAO-store-jj`
      }

      get userLocationName () {
        return `BAO-location-jj`
      }

      get flag () {
        return window.BAO.Utils.getElement('[data-country-flag]')
      }

      get stores () {
        return window.BAO.stores
      }

      get countries () {
        return window.BAO.countries
      }
    },
  )
}
